import { Wei } from '../../types'
import { usePositionValue } from './usePositionValue'
import { useTotalSpotConversionMarginValue } from './useTotalSpotConversionMarginValue'
import { useMemo } from 'react'
import { bn, e2w, isZero } from '../../utils/number'
import { useCurrencies, useMarketIndexes } from 'store/market/hooks'
import { Equal } from 'utils/equal'
import { Pow18 } from 'config'
import { useCurrencyBalance } from 'store/account/hooks'
import { getMarkPriceFromIndexes } from '../../utils/getMarkPriceFromIndexes'

export function useAssetsImpactOnLeverage(
  amount: string,
  symbolName: string,
): Wei {
  const positionValue = usePositionValue()
  const conversionMargin = useTotalSpotConversionMarginValue()
  const currencies = useCurrencies()
  const indexes = useMarketIndexes()
  const currencyBalance = useCurrencyBalance(symbolName)

  return useMemo(() => {
    if (isZero(positionValue) || isZero(conversionMargin)) {
      return '0'
    }

    if (!amount) return '0'

    if (!symbolName) return '0'

    let _amount = e2w(amount)
    if (_amount.gt(currencyBalance?.available ?? '0')) {
      _amount = bn(currencyBalance?.available ?? '0')
    }

    const { conversionRatio = 0 } =
      currencies.find((v) => Equal.upperCase(v.name, symbolName)) ?? {}
    const markPrice = getMarkPriceFromIndexes(indexes, symbolName)
    const changeValue = _amount
      .mul(e2w(conversionRatio))
      .mul(e2w(markPrice))
      .div(Pow18)
      .div(Pow18)
    const finalMargin = conversionMargin.sub(changeValue)

    const value = e2w(positionValue).div(finalMargin)
    return value.toString()
  }, [positionValue, conversionMargin, currencies, indexes, amount, symbolName])
}
